import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  APP_CATALOG_ROUTE,
  APP_PRODUCT_ROUTE,
  FeatureToggleEnum,
  NUMBER_0,
  NUMBER_1,
  NUMBER_2,
  NUMBER_3,
  NUMBER_500,
} from '~shared/constants';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services/price-type.service';
import {
  CartState,
  DeleteProductFromMiniCart,
  selectCurrency,
  UpdateQuantity,
} from '~shared/modules/shopping-cart-store';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import * as fromGlobalCart from '~shared/modules/shopping-cart-global/store';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { MiniCartApplicationEvent } from '~shared/modules/application-event/models/mini-cart-application-event.model';
import { ApplicationMiniCartEventTypeEnum } from '~shared/modules/application-event/constants';
import { GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { RemoveFromCartApplicationEvent } from '~shared/modules/application-event/models';
import { PDPNavigatorService } from '~shared/modules/pdp-navigator/pdp-navigator.service';

@UntilDestroy()
@Component({
  selector: 'app-mini-shopping-cart',
  templateUrl: './mini-shopping-cart.component.html',
  styleUrls: ['./mini-shopping-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniShoppingCartComponent implements OnChanges {
  @Input() cartData;
  @Input() showMiniCart$: boolean;
  @Input() isNavigationuBarEnabledV2: boolean;
  @Output() showMiniCart = new EventEmitter<{}>();
  @Output() trackGoToCartClick = new EventEmitter<boolean>();

  productRoute: string;
  productLink = `/${APP_PRODUCT_ROUTE}/`;
  deleteFlag = false;
  productName: string;
  productId: string;
  deletedProduct: string;
  accountType: PriceTypes;
  isConsumer: boolean;
  shoppingCartData;
  shoppingCartOverflow: boolean;
  catalogRoute = `/${APP_CATALOG_ROUTE}`;
  queryParams = { 'f.promotion': true };
  isQuantityUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showUndoOption = false;
  undoCart = false;
  lastOffer;
  b2bOfferModelValue = NUMBER_2;
  public currency$: Observable<string>;
  public isValueAddedServicesFFEnabled$ = this.featureFlagService.isFeatureEnabledAndReady(
    FeatureToggleEnum.FF_CART_WEB_APP_BUYER_VALUE_ADDED_SERVICES
  );

  constructor(
    private priceTypeService: PriceTypeService,
    private store$: Store<CartState>,
    private eRef: ElementRef,
    private applicationEventService: ApplicationEventService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private PDPNavigator: PDPNavigatorService
  ) {
    this.currency$ = this.store$.pipe(select(selectCurrency));
  }

  @HostListener('document:click', ['$event'])
  clickOut(event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showMiniCart.emit({ value: false, timer: NUMBER_0 });
    }
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: any) {
    if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
      this.shoppingCartOverflow = false;
    } else {
      this.shoppingCartOverflow = this.shoppingCartData?.offers?.length > NUMBER_3;
    }
  }

  ngOnChanges() {
    this.shoppingCartData = { ...this.cartData };
    this.shoppingCartOverflow = this.shoppingCartData?.offers?.length > NUMBER_3;
    this.accountType = this.priceTypeService.priceType$.getValue();
    this.isConsumer = this.accountType === PriceTypes.b2c;
    if (!this.showMiniCart$) {
      this.deleteFlag = this.undoCart = false;
    }
  }

  onRemove(offer) {
    if (offer.quantity <= NUMBER_1) {
      this.deleteItemFromCart(offer);
    } else {
      this.updateQuantityOfItem(offer);
    }
    this.applicationEventService.publish(
      new MiniCartApplicationEvent(ApplicationMiniCartEventTypeEnum.DeleteIconClicked, offer.item?.id)
    );
  }

  deleteItemFromCart(offer) {
    this.shoppingCartData.offers = this.shoppingCartData.offers.filter((item) => item.item.id !== offer?.item.id);
    const payload = {
      cartLineId: offer.cartLineId,
      id: offer.offer,
      offerLine: offer,
      accountType: this.accountType,
    };
    this.applicationEventService.publish(
      new RemoveFromCartApplicationEvent(
        payload.id,
        payload.offerLine,
        offer.valueAddedServices?.length > 0
          ? ApplicationMiniCartEventTypeEnum.removedProductAndServiceLabel
          : ApplicationMiniCartEventTypeEnum.removedProductLabel
      )
    );
    this.store$.dispatch(new DeleteProductFromMiniCart(payload));
    if (this.shoppingCartData.offers.length < NUMBER_1) {
      this.showUndoOption = true;
      this.lastOffer = offer;
    }
    this.showDeleteMessage(offer);
  }

  updateQuantityOfItem(offer) {
    this.productId = offer?.item?.id;
    this.isQuantityUpdate.next(true);
    const payload = {
      id: offer.offer,
      quantity: offer.quantity - NUMBER_1,
      accountType: this.accountType,
      trackingData: offer.trackingData,
    };
    this.store$.dispatch(new UpdateQuantity(payload));
    setTimeout(() => {
      this.isQuantityUpdate.next(false);
    }, NUMBER_500);
  }

  showDeleteMessage(offer) {
    this.productName = offer?.item?.name;
    this.productRoute = `${APP_PRODUCT_ROUTE}/${offer?.item.id}`;
    this.deletedProduct = offer?.item.id;
    this.deleteFlag = true;
  }

  undoLastDeleteFromCart() {
    const payload = {
      offer: this.lastOffer.offer,
      quantity: NUMBER_1,
      productId: this.productId,
      accountType: this.accountType,
    };
    this.store$.dispatch(new fromGlobalCart.AddToCart(payload));
    this.deleteFlag = this.showUndoOption = false;
    this.undoCart = true;
    this.applicationEventService.publish(
      new MiniCartApplicationEvent(ApplicationMiniCartEventTypeEnum.UndoClicked, GaTrackingEventLabel.FromMiniCart)
    );
  }

  onMouseHover() {
    this.showMiniCart.emit({ value: true, timer: null });
  }

  onMouseLeave() {
    this.showMiniCart.emit({ value: false, timer: NUMBER_500 });
  }

  onClickGoToCart() {
    this.trackGoToCartClick.emit(true);
    this.onClickCloseCart();
  }

  onClickCloseCart() {
    this.showMiniCart.emit({ value: false, timer: NUMBER_0 });
  }

  hasValueAddedServices(): boolean {
    return !!(this.cartData.totals?.valueAddedServicesTotal || this.cartData.totals?.valueAddedServicesTotalNet);
  }

  trackProductNameClick(id: string) {
    this.applicationEventService.publish(
      new MiniCartApplicationEvent(ApplicationMiniCartEventTypeEnum.ProductNameClicked, id)
    );
    this.onClickCloseCart();
  }

  handleProductClick(e: MouseEvent, productId: string, query?: string) {
    this.PDPNavigator.navigateToPDP(e, productId, query).subscribe();
  }
}
